"use client"

import { useState, useEffect, useMemo } from "react"
import axios from "axios"
import { ArrowUpward, ArrowDownward, Edit, Delete } from "@mui/icons-material"
import {
  Box,
  Card,
  CardContent,
  Button,
  Typography,
  Grid,
  FormControl,
  Select,
  MenuItem,
  TextField,
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  CircularProgress,
  Alert,
} from "@mui/material"
import AddSaleModal from "../../components/Sales/AddSaleModal"
import EditSaleModal from "../../components/Sales/EditSaleModal"
import DispatchModal from "../../components/Sales/DispatchModal"
import ConfirmationDialog from "../../components/Common/ConfirmationDialog"
import { useOutletContext } from "react-router-dom"
import BaseURL from "../../api/AppURL";
import Can from "../../components/Can";

const SaleManage = () => {
  const [sales, setSales] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [searchTerm, setSearchTerm] = useState("")
  const [entriesPerPage, setEntriesPerPage] = useState(25)
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "ascending" })
  const [downloading, setDownloading] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)

  // Modal states
  const [addModalOpen, setAddModalOpen] = useState(false)
  const [editModalOpen, setEditModalOpen] = useState(false)
  const [currentSaleId, setCurrentSaleId] = useState(null)
  const [dispatchModalOpen, setDispatchModalOpen] = useState(false)

  // Confirmation Dialog states
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false)
  const [saleToDeleteId, setSaleToDeleteId] = useState(null)

  const { showSnackbar } = useOutletContext()

  const fetchSales = async () => {
    setLoading(true)
    setError(null)
    try {
      const token = localStorage.getItem("auth_token")
      const response = await axios.get(`${BaseURL}/all/sale`, {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
      })
      const fetchedData = Array.isArray(response.data.sales) ? response.data.sales : []
      setSales(fetchedData)
      console.log("Sales fetched successfully. Total items:", fetchedData.length)
    } catch (err) {
      console.error("Error fetching sales:", err)
      setError("Failed to load sales. Please try again later.")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchSales()
  }, [])

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value)
    setCurrentPage(1)
  }

  const handleEntriesChange = (event) => {
    setEntriesPerPage(event.target.value)
    setCurrentPage(1)
  }

  const requestSort = (key) => {
    let direction = "ascending"
    if (sortConfig.key === key && sortConfig.direction === "ascending") {
      direction = "descending"
    }
    setSortConfig({ key, direction })
  }

  const sortedSales = useMemo(() => {
    const sortableItems = [...sales]
    if (sortConfig.key === null) return sortableItems

    sortableItems.sort((a, b) => {
      let aValue = a[sortConfig.key]
      let bValue = b[sortConfig.key]

      // Handle nested properties like customer.name or customer.mobile_no
      if (sortConfig.key === "customer_name") {
        aValue = a.customer?.name || ""
        bValue = b.customer?.name || ""
      } else if (sortConfig.key === "customer_mobile") {
        aValue = a.customer?.mobile_no || ""
        bValue = b.customer?.mobile_no || ""
      }

      if (aValue < bValue) {
        return sortConfig.direction === "ascending" ? -1 : 1
      }
      if (aValue > bValue) {
        return sortConfig.direction === "ascending" ? 1 : -1
      }
      return 0
    })
    return sortableItems
  }, [sales, sortConfig])

  const filteredSales = useMemo(() => {
    return sortedSales.filter((sale) => {
      const searchLower = searchTerm.toLowerCase()
      const customerName = sale.customer?.name || ""
      const customerMobile = sale.customer?.mobile_no || ""

      return (
        String(sale.id).toLowerCase().includes(searchLower) ||
        String(sale.chalan_no || "")
          .toLowerCase()
          .includes(searchLower) ||
        customerName.toLowerCase().includes(searchLower) ||
        customerMobile.toLowerCase().includes(searchLower) ||
        String(sale.sale_date || "")
          .toLowerCase()
          .includes(searchLower) ||
        String(sale.delivery_date || "")
          .toLowerCase()
          .includes(searchLower)
      )
    })
  }, [sortedSales, searchTerm])

  const indexOfLastSale = currentPage * entriesPerPage
  const indexOfFirstSale = indexOfLastSale - entriesPerPage
  const displayedSales = filteredSales.slice(indexOfFirstSale, indexOfLastSale)
  const totalPages = Math.ceil(filteredSales.length / entriesPerPage)

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return null
    return sortConfig.direction === "ascending" ? <ArrowUpward fontSize="small" /> : <ArrowDownward fontSize="small" />
  }

  const handleNextPage = () => {
    setCurrentPage((prevPage) => Math.min(prevPage + 1, totalPages))
  }

  const handlePreviousPage = () => {
    setCurrentPage((prevPage) => Math.max(prevPage - 1, 1))
  }

  const totalEntries = filteredSales.length
  const startEntry = totalEntries === 0 ? 0 : indexOfFirstSale + 1
  const endEntry = Math.min(indexOfLastSale, totalEntries)

  const handleChalanDownload = async (saleId) => {
    try {
      const token = localStorage.getItem("auth_token")
      const response = await axios.get(
        `${BaseURL}/sales/chalan/download/${saleId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "application/pdf",
          },
          responseType: "blob",
        }
      )
      // Try to get filename from headers; fallback to default
      let filename = `chalan_${saleId}.pdf`
      const contentDisposition =
        response.headers["content-disposition"] || response.headers["Content-Disposition"]
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?([^"]+)"?/)
        if (match && match[1]) filename = match[1]
      }
      // Create a blob link and trigger download
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement("a")
      link.href = url
      link.setAttribute("download", filename)
      document.body.appendChild(link)
      link.click()
      link.parentNode.removeChild(link)
      window.URL.revokeObjectURL(url)
      showSnackbar("Chalan PDF downloaded!", "success")
    } catch (err) {
      showSnackbar("Failed to download chalan PDF.", "error")
    }
  }

  const handleInvoiceDownload = async (saleId) => {
    try {
      const token = localStorage.getItem("auth_token")
      const response = await axios.get(
        `${BaseURL}/sales/invoice/download/${saleId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "application/pdf",
          },
          responseType: "blob",
        }
      )
      // Try to get filename from headers; fallback to default
      let filename = `invoice_${saleId}.pdf`
      const contentDisposition =
        response.headers["content-disposition"] || response.headers["Content-Disposition"]
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?([^"]+)"?/)
        if (match && match[1]) filename = match[1]
      }
      // Create a blob link and trigger download
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement("a")
      link.href = url
      link.setAttribute("download", filename)
      document.body.appendChild(link)
      link.click()
      link.parentNode.removeChild(link)
      window.URL.revokeObjectURL(url)
      showSnackbar("Invoice PDF downloaded!", "success")
    } catch (err) {
      showSnackbar("Failed to download invoice PDF.", "error")
    }
  }


  const renderPageNumbers = () => {
    const pageNumbers = []
    for (let i = 1; i <= totalPages; i++) {
      pageNumbers.push(
        <Button
          key={i}
          variant={currentPage === i ? "contained" : "outlined"}
          color={currentPage === i ? "primary" : "inherit"}
          onClick={() => setCurrentPage(i)}
          sx={{ minWidth: 40, px: 1 }}
        >
          {i}
        </Button>,
      )
    }
    return pageNumbers
  }

  const handleDispatchClick = () => {
    setDispatchModalOpen(true)
  }

  const handleDispatchModalClose = () => {
    setDispatchModalOpen(false)
  }


  const handleDispatchSuccess = (message) => {
    showSnackbar(message, "success")
    fetchSales()
  }

  const handleAddSaleClick = () => {
    setAddModalOpen(true)
  }

  const handleAddModalClose = () => {
    setAddModalOpen(false)
  }

  const handleSaleAddedSuccess = (message) => {
    showSnackbar(message, "success")
    fetchSales()
  }

  const handleEditSaleClick = (saleId) => {
    setCurrentSaleId(saleId)
    setEditModalOpen(true)
  }

  const handleEditModalClose = () => {
    setEditModalOpen(false)
    setCurrentSaleId(null)
  }

  const handleSaleUpdatedSuccess = (message) => {
    showSnackbar(message, "success")
    fetchSales()
  }

  // Open confirmation dialog
  const handleDeleteClick = (saleId) => {
    setSaleToDeleteId(saleId)
    setConfirmDialogOpen(true)
  }

  // Close confirmation dialog
  const handleConfirmDialogClose = () => {
    setConfirmDialogOpen(false)
    setSaleToDeleteId(null)
  }

  // Execute delete after confirmation
  const handleConfirmDelete = async () => {
    if (!saleToDeleteId) return

    setConfirmDialogOpen(false) // Close the dialog immediately
    console.log("Attempting to delete sale with ID:", saleToDeleteId)

    try {
      const token = localStorage.getItem("auth_token")
      const response = await axios.delete(`${BaseURL}/delete/sale/${saleToDeleteId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
      })

      console.log("DELETE API Response:", response.data)

      const isSuccess = response.data.success === true || response.data.message?.includes("successfully")

      if (isSuccess) {
        showSnackbar(response.data.message || "Sale deleted successfully!", "success")
        console.log("Sale deleted successfully. Optimistically updating UI...")


        //নিচের এই অংশটা না লিখলেও code কাজ করে
        // setSales((prevSales) => {
        //   const updatedSales = prevSales.filter((s) => {
        //     const isMatch = String(s.id) === String(saleToDeleteId)
        //     console.log(
        //       `Comparing s.id: ${s.id} (type: ${typeof s.id}) with saleToDeleteId: ${saleToDeleteId} (type: ${typeof saleToDeleteId}). Match: ${isMatch}`,
        //     )
        //     return !isMatch
        //   })
        //   console.log("Sales after optimistic update. New count:", updatedSales.length)
        //   return updatedSales
        // })

        await fetchSales()
        console.log("Table re-synced with backend after deletion.")
      } else {
        showSnackbar(response.data.message || "Failed to delete sale.", "error")
        console.log("Failed to delete sale (API reported failure or unexpected response):", response.data.message)
      }
    } catch (err) {
      console.error("Error deleting sale:", err)
      showSnackbar("An error occurred while deleting the sale.", "error")
    } finally {
      setSaleToDeleteId(null)
    }
  }

  return (
    <Box>
      <Card sx={{ mb: 3 }}>
        <CardContent>
          {/* Action Buttons Section */}
          <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap" }}>
            <Can permission="sale.add">
              <Button variant="contained" onClick={handleAddSaleClick}>
                Add Sales
              </Button>
            </Can>
            <Can permission="dispatch order">
              <Button variant="contained" onClick={handleDispatchClick}>Dispatch Orders</Button>
            </Can>
            <Button variant="contained">Sales Archive</Button>
            {/* ৳ 0 TK text with lighter style */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography variant="h7" sx={{ fontWeight: "normal", color: "text.secondary" }}>
                ৳ 0 TK
              </Typography>
            </Box>
          </Box>

          {/* Search and Entries Per Page Section */}
          <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6} md={4}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography variant="body2">Show</Typography>
                <FormControl size="small" sx={{ minWidth: 80 }}>
                  <Select
                    value={entriesPerPage}
                    onChange={handleEntriesChange}
                    displayEmpty
                    inputProps={{ "aria-label": "Entries per page" }}
                  >
                    <MenuItem value={10}>10</MenuItem>
                    <MenuItem value={25}>25</MenuItem>
                    <MenuItem value={50}>50</MenuItem>
                    <MenuItem value={100}>100</MenuItem>
                  </Select>
                </FormControl>
                <Typography variant="body2">entries</Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={8}>
              <TextField
                label="Search"
                variant="outlined"
                size="small"
                value={searchTerm}
                onChange={handleSearchChange}
                fullWidth
                sx={{ ml: { sm: "auto" } }}
              />
            </Grid>
          </Grid>

          {/* Table Section */}
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Alert severity="error">{error}</Alert>
          ) : (
            <TableContainer component={Paper} elevation={0} variant="outlined">
              <Box sx={{ overflowX: "auto", minWidth: 650 }}>
                <Table aria-label="sales table">
                  <TableHead sx={{ bgcolor: "#f5f5f5" }}>
                    <TableRow>
                      <TableCell
                        sx={{ fontWeight: "bold", width: "5%", borderRight: "1px solid #e0e0e0", whiteSpace: "nowrap" }}
                      >
                        Sl
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: "bold",
                          width: "15%",
                          borderRight: "1px solid #e0e0e0",
                          whiteSpace: "nowrap",
                        }}
                      >
                        <Button
                          onClick={() => requestSort("id")}
                          sx={{ textTransform: "none", p: 0, minWidth: 0, whiteSpace: "nowrap" }}
                        >
                          Invoice No {getSortIcon("id")}
                        </Button>
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: "bold",
                          width: "15%",
                          borderRight: "1px solid #e0e0e0",
                          whiteSpace: "nowrap",
                        }}
                      >
                        <Button
                          onClick={() => requestSort("chalan_no")}
                          sx={{ textTransform: "none", p: 0, minWidth: 0, whiteSpace: "nowrap" }}
                        >
                          Chalan No {getSortIcon("chalan_no")}
                        </Button>
                      </TableCell>
                      {/* MPO (Customer) changed to MPO */}
                      <TableCell
                        sx={{
                          fontWeight: "bold",
                          width: "25%",
                          borderRight: "1px solid #e0e0e0",
                          whiteSpace: "nowrap",
                          minWidth: "180px",
                        }}
                      >
                        <Button
                          onClick={() => requestSort("customer_name")}
                          sx={{ textTransform: "none", p: 0, minWidth: 0, whiteSpace: "nowrap" }}
                        >
                          MPO {getSortIcon("customer_name")}
                        </Button>
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: "bold",
                          width: "15%",
                          borderRight: "1px solid #e0e0e0",
                          whiteSpace: "nowrap",
                        }}
                      >
                        <Button
                          onClick={() => requestSort("sale_date")}
                          sx={{ textTransform: "none", p: 0, minWidth: 0, whiteSpace: "nowrap" }}
                        >
                          Sale Date {getSortIcon("sale_date")}
                        </Button>
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: "bold",
                          width: "15%",
                          borderRight: "1px solid #e0e0e0",
                          whiteSpace: "nowrap",
                        }}
                      >
                        <Button
                          onClick={() => requestSort("delivery_date")}
                          sx={{ textTransform: "none", p: 0, minWidth: 0, whiteSpace: "nowrap" }}
                        >
                          Delivery Date {getSortIcon("delivery_date")}
                        </Button>
                      </TableCell>
                      <TableCell sx={{ fontWeight: "bold", width: "10%", whiteSpace: "nowrap" }}>Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {displayedSales.length > 0 ? (
                      displayedSales.map((sale, index) => (
                        <TableRow key={sale.id || index}>
                          <TableCell sx={{ borderRight: "1px solid #e0e0e0" }}>
                            {indexOfFirstSale + index + 1}
                          </TableCell>
                          <TableCell sx={{ borderRight: "1px solid #e0e0e0" }}>INV-{sale.id}</TableCell>
                          {/* Chalan No column updated to use sale.id */}
                          <TableCell sx={{ borderRight: "1px solid #e0e0e0" }}>CH-{sale.id}</TableCell>
                          {/* MPO column data display */}
                          <TableCell sx={{ borderRight: "1px solid #e0e0e0", whiteSpace: "nowrap" }}>
                            {sale.customer?.name} ({sale.customer?.mobile_no})
                          </TableCell>
                          <TableCell sx={{ borderRight: "1px solid #e0e0e0" }}>{sale.sale_date}</TableCell>
                          <TableCell sx={{ borderRight: "1px solid #e0e0e0" }}>{sale.delivery_date}</TableCell>
                          <TableCell>
                            <Box sx={{ display: "flex", gap: 1 }}>
                              <Can permission="sale.edit">
                                <Button variant="outlined" size="small" onClick={() => handleEditSaleClick(sale.id)}>
                                  <Edit fontSize="small" />
                                </Button>
                              </Can>
                              <Button
                                variant="contained"
                                size="small"
                                sx={{ backgroundColor: "#4CAF50", "&:hover": { backgroundColor: "#45a049" } }}
                                onClick={() => handleChalanDownload(sale.id)}
                              >
                                CH
                              </Button>
                              <Button
                                variant="contained"
                                size="small"
                                sx={{ backgroundColor: "#4CAF50", "&:hover": { backgroundColor: "#45a049" } }}
                                onClick={() => handleInvoiceDownload(sale.id)}
                              >
                                INV
                              </Button>
                              {/* Delete button moved to the end */}
                              <Can permission="sale.delete">
                                <Button
                                  variant="contained"
                                  color="error"
                                  size="small"
                                  onClick={() => handleDeleteClick(sale.id)}
                                >
                                  <Delete fontSize="small" />
                                </Button>
                              </Can>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={7} sx={{ textAlign: "center", py: 4 }}>
                          No sales found.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </Box>
            </TableContainer>
          )}
          {/* Pagination Section */}
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              justifyContent: "space-between",
              alignItems: { xs: "center", sm: "center" },
              gap: 2,
              mt: 2,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              Showing {startEntry} to {endEntry} of {totalEntries} entries
            </Typography>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button variant="outlined" onClick={handlePreviousPage} disabled={currentPage === 1}>
                Prev
              </Button>
              {renderPageNumbers()}
              <Button variant="outlined" onClick={handleNextPage} disabled={currentPage === totalPages}>
                Next
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>
      {/* Add Sale Modal */}
      <AddSaleModal open={addModalOpen} onClose={handleAddModalClose} onSaveSuccess={handleSaleAddedSuccess} />
      <DispatchModal open={dispatchModalOpen} onClose={handleDispatchModalClose} onSaveSuccess={handleDispatchSuccess} />
      {/* Edit Sale Modal */}
      {editModalOpen && (
        <EditSaleModal
          open={editModalOpen}
          onClose={handleEditModalClose}
          onUpdateSuccess={handleSaleUpdatedSuccess}
          saleId={currentSaleId}
        />
      )}
      {/* Confirmation Dialog for Delete */}
      <ConfirmationDialog
        open={confirmDialogOpen}
        onClose={handleConfirmDialogClose}
        onConfirm={handleConfirmDelete}
        title="Are you sure?"
        message="Delete This Data?"
        confirmText="Yes, delete it!"
        cancelText="Cancel"
      />
    </Box>
  )
}

export default SaleManage
